/**
 * Assistant UI store (djin-009 slice 1).
 *
 * Holds the chat panel's open state + in-session conversation so the floating launcher
 * and the sidebar entry both drive the same panel, and the transcript survives closing/
 * reopening the panel (the widget is mounted once in AppLayout, for the whole session).
 */
import { create } from 'zustand';

export interface AssistantToolCall {
  name: string;
  input?: unknown;
  result?: string;
  isError?: boolean;
}

export interface AssistantChartSeries { name: 'avg' | 'min' | 'max'; points: [number, number][] }
export interface AssistantChartBlock {
  id: string; title: string; unit: string | null; interval: '15m' | '1h' | '6h' | '1d';
  coveredFrom: string; coveredTo: string; truncated: boolean; series: AssistantChartSeries[];
}

export interface AssistantTurn {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  toolCalls?: AssistantToolCall[];
  charts?: AssistantChartBlock[];
}

const FAV_KEY = 'djin.assistant.favorites';
const MAX_FAVORITES = 20;
const loadFavorites = (): string[] => {
  try {
    const raw = localStorage.getItem(FAV_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
};
const saveFavorites = (favs: string[]): void => {
  try {
    localStorage.setItem(FAV_KEY, JSON.stringify(favs));
  } catch {
    /* storage unavailable — favorites are best-effort */
  }
};

interface AssistantState {
  isOpen: boolean;
  turns: AssistantTurn[];
  loading: boolean;
  /** Server-side conversation id (row B1). In memory only — never persisted. */
  conversationId: string | null;
  setConversationId: (id: string | null) => void;
  /** Saved prompts (persisted to localStorage), newest first. */
  favorites: string[];
  open: () => void;
  close: () => void;
  toggle: () => void;
  addTurn: (t: AssistantTurn) => void;
  setLoading: (b: boolean) => void;
  reset: () => void;
  toggleFavorite: (text: string) => void;
  removeFavorite: (text: string) => void;
  clearFavorites: () => void;
  // Streaming mutators (djin-009 slice 2) — update one in-flight assistant turn by id.
  startAssistant: (id: string) => void;
  appendText: (id: string, delta: string) => void;
  addToolName: (id: string, name: string) => void;
  addChart: (id: string, chart: AssistantChartBlock) => void;
  finalizeAssistant: (id: string, text: string, toolCalls?: AssistantToolCall[]) => void;
  removeTurn: (id: string) => void;
}

const patch = (turns: AssistantTurn[], id: string, fn: (t: AssistantTurn) => AssistantTurn) =>
  turns.map((t) => (t.id === id ? fn(t) : t));

export const useAssistantStore = create<AssistantState>((set) => ({
  isOpen: false,
  turns: [],
  loading: false,
  conversationId: null,
  setConversationId: (id) => set({ conversationId: id }),
  favorites: loadFavorites(),
  open: () => set({ isOpen: true }),
  close: () => set({ isOpen: false }),
  toggle: () => set((s) => ({ isOpen: !s.isOpen })),
  addTurn: (t) => set((s) => ({ turns: [...s.turns, t] })),
  setLoading: (b) => set({ loading: b }),
  reset: () => set({ turns: [], conversationId: null }),
  toggleFavorite: (text) =>
    set((s) => {
      const has = s.favorites.includes(text);
      const favorites = has
        ? s.favorites.filter((f) => f !== text)
        : [text, ...s.favorites].slice(0, MAX_FAVORITES);
      saveFavorites(favorites);
      return { favorites };
    }),
  removeFavorite: (text) =>
    set((s) => {
      const favorites = s.favorites.filter((f) => f !== text);
      saveFavorites(favorites);
      return { favorites };
    }),
  clearFavorites: () => {
    saveFavorites([]);
    set({ favorites: [] });
  },
  startAssistant: (id) => set((s) => ({ turns: [...s.turns, { id, role: 'assistant', text: '', toolCalls: [] }] })),
  appendText: (id, delta) => set((s) => ({ turns: patch(s.turns, id, (t) => ({ ...t, text: t.text + delta })) })),
  addToolName: (id, name) =>
    set((s) => ({
      turns: patch(s.turns, id, (t) =>
        (t.toolCalls ?? []).some((c) => c.name === name)
          ? t
          : { ...t, toolCalls: [...(t.toolCalls ?? []), { name }] },
      ),
    })),
  addChart: (id, chart) => set((s) => ({ turns: patch(s.turns, id, (t) => ({ ...t, charts: [...(t.charts ?? []), chart] })) })),
  finalizeAssistant: (id, text, toolCalls) =>
    set((s) => ({ turns: patch(s.turns, id, (t) => ({ ...t, text: text || t.text, toolCalls: toolCalls ?? t.toolCalls })) })),
  removeTurn: (id) => set((s) => ({ turns: s.turns.filter((t) => t.id !== id) })),
}));
